import { Header } from './components/Header';
import { CartPage } from './components/CartPage';
import { CheckoutPage } from './components/CheckoutPage';
import { CookieConsent } from './components/CookieConsent';
import { GrowthFunnelSection } from './components/GrowthFunnelSection';
import { LegalFooter } from './components/LegalFooter';
import { LegalPage } from './components/LegalPage';
import { LiveDropSection } from './components/LiveDropSection';
import { NewsletterBlock } from './components/NewsletterBlock';
import { MobileStickyBar } from './components/MobileStickyBar';
import { NotFoundPage } from './components/NotFoundPage';
import { OrderThanksPage } from './components/OrderThanksPage';
import { ChoiceQuizPage } from './components/ChoiceQuizPage';
import { ProductDetailPage } from './components/ProductDetailPage';
import { ProductExplorerPage } from './components/ProductExplorerPage';
import { ReadinessSection } from './components/ReadinessSection';
import { ReturnRequestPage } from './components/ReturnRequestPage';
import { RouteMetadata } from './components/RouteMetadata';
import { SeoHubSection } from './components/SeoHubSection';
import { SeoLandingPage } from './components/SeoLandingPage';
import { ScrollDepthTracker } from './components/ScrollDepthTracker';
import { SupplierMatrix } from './components/SupplierMatrix';
import { findLegalPage, normalizePath } from './data/legalPages';
import { findCollectionCategory, findLiveDropProduct } from './data/liveDrop';
import { categories } from './data/products';
import { findSeoGuidePage } from './data/seoPages';
import { homeFaqs } from './data/faqContentBank';
import './styles.css';

const categoryHighlights: Record<string, string> = {
  startersets: 'Alles om direct met twee of vier spelers te beginnen.',
  paddles: 'Kies op niveau, speelgevoel en budget.',
  ballen: 'Indoor en outdoor ballen voor training en recreatie.',
  'netten-en-belijning': 'Zet snel een veld op voor thuis, school of club.',
  'schoenen-en-bescherming': 'Bescherming en court-shoe advies voor veilig bewegen.',
  'tassen-accessoires': 'Grips, tape, tassen en handige add-ons.',
  clubpakketten: 'Materiaaladvies en offerte voor groepen.'
};

function ShopCategorySection() {
  return (
    <section className="section shopCategorySection" id="assortiment">
      <div className="sectionHeading compactHeading">
        <p className="eyebrow">Shop per categorie</p>
        <h2>Snel naar de producten die je nodig hebt.</h2>
        <p className="sectionIntro">
          Begin met een complete set, kies een betere paddle of stel materiaal samen voor een les, clinic of vereniging.
        </p>
      </div>
      <div className="shopCategoryGrid">
        {categories.map((category) => (
          <a href={`/collecties/${category.key}/`} key={category.key}>
            <span>{category.label}</span>
            <strong>{categoryHighlights[category.key]}</strong>
          </a>
        ))}
      </div>
    </section>
  );
}

function BrandImageSection() {
  return (
    <section className="section brandImageSection" aria-labelledby="brand-images-title">
      <div className="sectionHeading compactHeading">
        <p className="eyebrow">Materiaal en speelplek</p>
        <h2 id="brand-images-title">Van eerste paddle tot een compleet veld.</h2>
        <p className="sectionIntro">Bekijk het materiaal in een rustige productopstelling en in de context van een club of school.</p>
      </div>
      <div className="brandImageGrid">
        <figure className="brandImageLead">
          <img src="/images/brand/propickleball-paddles-640.webp" srcSet="/images/brand/propickleball-paddles-640.webp 640w, /images/brand/propickleball-paddles.webp 1280w" sizes="(max-width: 760px) calc(100vw - 36px), 55vw" alt="Twee zwarte pickleballpaddles met drie gele pickleballballen" width="1280" height="853" loading="lazy" decoding="async" />
          <figcaption>Paddles en ballen</figcaption>
        </figure>
        <figure>
          <img src="/images/brand/propickleball-gear-640.webp" srcSet="/images/brand/propickleball-gear-640.webp 640w, /images/brand/propickleball-gear.webp 1280w" sizes="(max-width: 760px) calc(100vw - 36px), 35vw" alt="Pickleballpaddles, indoor- en outdoorballen, grips, handdoek en nettas" width="1280" height="853" loading="lazy" decoding="async" />
          <figcaption>Materiaal voor training</figcaption>
        </figure>
        <figure>
          <img src="/images/brand/propickleball-club-court-640.webp" srcSet="/images/brand/propickleball-club-court-640.webp 640w, /images/brand/propickleball-club-court.webp 1280w" sizes="(max-width: 760px) calc(100vw - 36px), 35vw" alt="Draagbaar pickleballnet met paddles en ballen in een sporthal" width="1280" height="853" loading="lazy" decoding="async" />
          <figcaption>Opstelling voor club of school</figcaption>
        </figure>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <main>
        <Header />

        <ShopCategorySection />
        <BrandImageSection />
        <GrowthFunnelSection />
        <LiveDropSection />

        <section className="section split" id="keuzehulp">
          <div>
            <p className="eyebrow">Hulp bij kiezen</p>
            <h2>Niet zeker welke set, paddle of bal past?</h2>
            <p>
              Gebruik de keuzehulp als je twijfelt tussen indoor of outdoor, starterset of losse paddle, of materiaal voor meerdere spelers. Daarna ga je direct naar de juiste productgroep.
            </p>
            <div className="ctaRow inlineCtaRow">
              <a className="primaryCta" href="/keuzehulp/">Start keuzehulp</a>
              <a className="secondaryDarkCta" href="/producten/">Bekijk alle producten</a>
            </div>
          </div>
          <div className="checklist">
            <h3>Populaire koopvragen</h3>
            <ul>
              <li>Welke starterset is logisch voor twee of vier spelers?</li>
              <li>Welke bal heb ik nodig voor sporthal of buitenbaan?</li>
              <li>Wanneer kies ik een losse paddle in plaats van een complete set?</li>
              <li>Wat is handig voor gymles, vereniging of clinic?</li>
            </ul>
          </div>
        </section>

        <section className="section" id="clubs">
          <p className="eyebrow">Clubs & scholen</p>
          <h2>Pakketten voor lessen, clinics en verenigingen.</h2>
          <div className="categoryCards">
            {categories.map((category) => (
              <article key={category.key}>
                <h3>{category.label}</h3>
                <p>{category.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section" id="starten">
          <div className="sectionHeading compactHeading">
            <p className="eyebrow">Zo begin je</p>
            <h2>Starten met pickleball in 3 stappen.</h2>
            <p className="sectionIntro">Twijfel je waar te beginnen? Volg deze drie stappen en je hebt alles wat je nodig hebt.</p>
          </div>
          <div className="categoryCards stepCards">
            <article><h3><span className="stepNo">1</span> Kies je paddle</h3><p>Begin met een controle-paddle die bij je niveau past. Onze <a href="/keuzehulp/">keuzehulp</a> helpt je kiezen.</p></article>
            <article><h3><span className="stepNo">2</span> Kies indoor of outdoor ballen</h3><p>Speel je in de zaal of buiten? Dat bepaalt de bal. Lees <a href="/pickleball-ballen-indoor-outdoor/">indoor vs outdoor</a>.</p></article>
            <article><h3><span className="stepNo">3</span> Voeg een net of starterset toe</h3><p>Alleen spelen of met een groep? Een <a href="/collecties/startersets/">starterset</a> of draagbaar net maakt het compleet.</p></article>
          </div>
        </section>

        <section className="section" id="waarom">
          <div className="sectionHeading compactHeading">
            <p className="eyebrow">Waarom ProPickleball</p>
            <h2>Kiezen wordt makkelijk, kopen doe je pas als het klopt.</h2>
          </div>
          <div className="categoryCards">
            <article><h3>Eerlijke selectie</h3><p>Een overzichtelijke keuze zonder overweldigend assortiment of prijstrucs.</p></article>
            <article><h3>Advies voor beginners</h3><p>Keuzehulp en gidsen die uitleggen wat past bij jouw niveau en speelplek.</p></article>
            <article><h3>NL/BE service</h3><p>Nederlandstalige uitleg en service, gericht op spelers in Nederland en Belgie.</p></article>
            <article><h3>Clubs & scholen op maat</h3><p>Materiaaladvies en offerte voor verenigingen, scholen en bedrijven.</p></article>
          </div>
        </section>

        <SupplierMatrix />
        <SeoHubSection />
        <ReadinessSection />

        <section className="section" id="faq">
          <div className="sectionHeading compactHeading">
            <p className="eyebrow">Veelgestelde vragen</p>
            <h2>Wat beginners het vaakst vragen.</h2>
          </div>
          <div className="homeFaq">
            {homeFaqs.map((item) => (
              <details key={item.id} id={`faq-${item.id}`}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <NewsletterBlock />
      </main>
      <LegalFooter />
    </>
  );
}

function App() {
  const legalPage = findLegalPage(window.location.pathname);
  const seoPage = findSeoGuidePage(window.location.pathname);
  const liveDropProduct = findLiveDropProduct(window.location.pathname);
  const collectionCategory = findCollectionCategory(window.location.pathname);
  const normalizedPath = normalizePath(window.location.pathname);
  const orderMatch = normalizedPath.match(/^\/bestelling\/([^/]+)\/(?:bedankt|status)\/$/);

  return (
    <>
      <a
        className="skipLink"
        href="#main"
        onClick={(e) => {
          const m = document.querySelector('main');
          if (m) {
            e.preventDefault();
            m.setAttribute('tabindex', '-1');
            (m as HTMLElement).focus();
            m.scrollIntoView();
          }
        }}
      >
        Naar hoofdinhoud
      </a>
      {legalPage ? <LegalPage page={legalPage} /> : seoPage ? <SeoLandingPage page={seoPage} /> : orderMatch ? <OrderThanksPage orderNumber={decodeURIComponent(orderMatch[1])} /> : liveDropProduct ? <ProductDetailPage product={liveDropProduct} /> : collectionCategory ? <ProductExplorerPage initialCategory={collectionCategory} /> : normalizedPath === '/producten/' ? <ProductExplorerPage /> : normalizedPath === '/keuzehulp/' ? <ChoiceQuizPage /> : normalizedPath === '/winkelwagen/' ? <CartPage /> : normalizedPath === '/checkout/' ? <CheckoutPage /> : normalizedPath === '/retour-aanmelden/' ? <ReturnRequestPage /> : normalizedPath === '/' ? <HomePage /> : <NotFoundPage />}
      <RouteMetadata />
      <ScrollDepthTracker />
      <CookieConsent />
      <MobileStickyBar />
    </>
  );
}

export default App;
